import { useEffect, useState } from "react";
import {
  createInstance, getInstanceStatus,
  type CreateInstanceResponse, type InstanceStatus,
} from "../api";
import { Pill, type Tone } from "./ai/Primitives";

/** Provisioning states share one shape: a tone pill and a plain sentence. */
const PROVISION_TONE: Record<string, Tone> = {
  ingesting: "running", in_progress: "running", merged: "running",
  deploying: "running", propagating: "running", unknown: "neutral",
  ingest_done: "accent", ready: "done",
  ingest_failed: "error", deploy_failed: "error", failed: "error",
};
const PROVISION_STAGE: Record<string, string> = {
  ingesting: "Running", in_progress: "Running", merged: "Running",
  deploying: "Deploying", propagating: "Propagating", unknown: "Waiting",
  ingest_done: "Staged", ready: "Ready",
  ingest_failed: "Failed", deploy_failed: "Failed", failed: "Failed",
};



/**
 * Create-instance modal (P16.3). The flow:
 *   1. User fills name + description + domain_shape + archetype
 *   2. POST /api/admin/instances/create → returns PR number/url
 *   3. Modal switches to "Provisioning..." view, polls
 *      /api/admin/instances/<slug>/status every 5s
 *   4. When state="ready", redirect to /<slug>/
 *
 * Status states (from the Worker):
 *   - in_progress  → init-instance workflow running
 *   - merged       → PR merged, deploy-worker queued
 *   - deploying    → deploy-worker running
 *   - ready        → instance bundled into live Worker
 *   - failed       → workflow failed; show error
 */

export function CreateInstanceModal({
  open, onClose, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (slug: string) => void;
}) {
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");
  const [domainShape, setDomainShape] = useState<"informational" | "workflow">("workflow");
  const [archetype, setArchetype] = useState<"prose" | "code">("prose");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [created, setCreated] = useState<CreateInstanceResponse | null>(null);
  const [status, setStatus] = useState<InstanceStatus | null>(null);

  // Reset when re-opened
  useEffect(() => {
    if (!open) {
      setSlug(""); setDescription(""); setOwnerEmail(""); setOwnerPassword("");
      setDomainShape("workflow");
      setArchetype("prose"); setBusy(false); setErr(null);
      setCreated(null); setStatus(null);
    }
  }, [open]);

  // Poll status once submission succeeds. Branch passed so the Worker can
  // query GitHub for THIS instance's runs directly (immune to recent-runs
  // window — the source of "stuck at deploying" before this fix).
  useEffect(() => {
    if (!created) return;
    let cancelled = false;
    const terminal = new Set(["failed", "ingest_failed", "deploy_failed"]);
    const poll = async () => {
      try {
        const s = await getInstanceStatus(created.slug, created.branch);
        if (cancelled) return;
        setStatus(s);
        if (s.state === "ready") {
          onCreated(created.slug);
          return;
        }
        if (!terminal.has(s.state)) {
          setTimeout(poll, 5000);
        }
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [created, onCreated]);

  const submit = async () => {
    setErr(null);
    if (!/^[a-z][a-z0-9-]{1,40}$/.test(slug)) {
      setErr("Slug: 2-41 chars, lowercase letters/digits/hyphens, starting with a letter.");
      return;
    }
    if (description.trim().length < 10) {
      setErr("Description must be at least 10 chars.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(ownerEmail.trim())) {
      setErr("Owner email is required — the person who will run this instance.");
      return;
    }
    if (ownerPassword.length < 8) {
      setErr("Owner password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      const r = await createInstance({
        slug, description: description.trim(),
        domain_shape: domainShape, archetype,
        owner_email: ownerEmail.trim(), owner_password: ownerPassword,
      });
      setCreated(r);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        {!created ? (
          <CreateForm
            slug={slug} setSlug={setSlug}
            description={description} setDescription={setDescription}
            ownerEmail={ownerEmail} setOwnerEmail={setOwnerEmail}
            ownerPassword={ownerPassword} setOwnerPassword={setOwnerPassword}
            domainShape={domainShape} setDomainShape={setDomainShape}
            archetype={archetype} setArchetype={setArchetype}
            busy={busy} err={err}
            onSubmit={submit} onCancel={onClose}
          />
        ) : (
          <ProvisioningView created={created} status={status} err={err} onCancel={onClose} />
        )}
      </div>
    </div>
  );
}


function CreateForm({
  slug, setSlug, description, setDescription,
  ownerEmail, setOwnerEmail, ownerPassword, setOwnerPassword,
  domainShape, setDomainShape, archetype, setArchetype,
  busy, err, onSubmit, onCancel,
}: any) {
  return (
    <>
      <h2 className="modal-title">Create new instance</h2>
      <p className="modal-hint">
        Opens a PR on <code>techflouu/nexus</code> that scaffolds the instance,
        then CI auto-merges + deploys (~60s). You also set the instance
        <strong> owner</strong> here — they sign in with these credentials and
        run the instance; you (superadmin) don't operate it.
      </p>

      <label className="form-row">
        <span>Slug</span>
        <input
          type="text"
          value={slug}
          onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
          placeholder="lowercase-hyphen-slug"
          maxLength={41}
          autoFocus
          disabled={busy}
        />
        <small>
          {slug ? `URL: nexus.techflouu.com/${slug}/` : "Letters, digits, hyphens. Becomes part of the URL."}
        </small>
      </label>

      <label className="form-row">
        <span>Description</span>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="What's this instance for? (1-2 sentences)"
          rows={3}
          minLength={10}
          maxLength={500}
          disabled={busy}
        />
        <small>{description.length} / 500</small>
      </label>

      <label className="form-row">
        <span>Owner email</span>
        <input
          type="email"
          value={ownerEmail}
          onChange={(e: any) => setOwnerEmail(e.target.value)}
          placeholder="owner@company.com"
          disabled={busy}
        />
        <small>The person who will run this instance. They sign in with this.</small>
      </label>

      <label className="form-row">
        <span>Owner password</span>
        <input
          type="password"
          value={ownerPassword}
          onChange={(e: any) => setOwnerPassword(e.target.value)}
          placeholder="Initial password (min 8) — they can change it after"
          minLength={8}
          disabled={busy}
        />
      </label>

      <div className="form-row-grid">
        <label>
          <span>Domain shape</span>
          <select value={domainShape} onChange={e => setDomainShape(e.target.value as any)} disabled={busy}>
            <option value="workflow">workflow (skills can be derived)</option>
            <option value="informational">informational (chat-only)</option>
          </select>
        </label>
        <label>
          <span>Archetype</span>
          <select value={archetype} onChange={e => setArchetype(e.target.value as any)} disabled={busy}>
            <option value="prose">prose (PDF / HTML / Markdown)</option>
            <option value="code">code (TS / source files)</option>
          </select>
        </label>
      </div>

      {err && <div className="error-banner">{err}</div>}

      <div className="modal-actions">
        <button onClick={onCancel} disabled={busy}>Cancel</button>
        <button className="primary" onClick={onSubmit} disabled={busy || !slug || !description}>
          {busy ? "Opening PR…" : "Create"}
        </button>
      </div>
    </>
  );
}


function ProvisioningView({ created, status, err, onCancel }: {
  created: CreateInstanceResponse;
  status: InstanceStatus | null;
  err: string | null;
  onCancel: () => void;
}) {
  const state = status?.state || "in_progress";
  const stateLabel = ({
    in_progress: "Workflow running — scaffolding instance…",
    merged: "PR merged. Triggering deploy…",
    deploying: "Deploying Worker with new instance bundled…",
    propagating: "Deployed. Waiting for edge propagation…",
    ready: "Ready. Navigating to the new instance…",
    ingest_failed: "The scaffolding workflow failed.",
    deploy_failed: "The deploy workflow failed.",
    failed: "The workflow was cancelled or failed.",
    unknown: "Waiting for status…",
  } as Record<string, string>)[state] || state;

  const [copied, setCopied] = useState(false);
  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(created.bearer_token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard may be denied; user can select+copy manually */ }
  };

  return (
    <>
      <h2 className="modal-title">Provisioning <code>{created.slug}</code></h2>
      <div className="modal-hint">
        PR <a href={created.pr_url} target="_blank" rel="noreferrer">#{created.pr_number}</a> opened on
        branch <code>{created.branch}</code>.
      </div>

      {/* P16.5 — bearer token is shown ONCE. Save it before navigating. */}
      <div className="bearer-token-panel">
        <div className="bearer-token-label">
          MCP bearer token — <strong>save this now, shown once</strong>
        </div>
        <div className="bearer-token-row">
          <code className="bearer-token-value">{created.bearer_token}</code>
          <button onClick={copyToken} className="bearer-copy-btn">
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <div className="bearer-token-hint">{created.bearer_token_warning}</div>
      </div>

      <div className="provision-status">
        <div className="provision-state">
          <Pill tone={PROVISION_TONE[state] || "neutral"}
                dot={(PROVISION_TONE[state] || "neutral") === "running"}>
            {PROVISION_STAGE[state] || "Working"}
          </Pill>
          <span>{stateLabel}</span>
        </div>
        {status?.init && (
          <a href={status.init.url} target="_blank" rel="noreferrer" className="provision-link">
            Scaffold — {status.init.status}{status.init.conclusion ? ` (${status.init.conclusion})` : ""}
          </a>
        )}
        {status?.deploy && (
          <a href={status.deploy.url} target="_blank" rel="noreferrer" className="provision-link">
            Deploy — {status.deploy.status}{status.deploy.conclusion ? ` (${status.deploy.conclusion})` : ""}
          </a>
        )}
        {status?.bundle_generated_at && (state === "propagating" || state === "ready") && (
          <div className="provision-link" style={{ opacity: 0.7 }}>
            bundle generated: {status.bundle_generated_at}
          </div>
        )}
        {/* legacy back-compat */}
        {status?.run && !status.init && (
          <a href={status.run.url} target="_blank" rel="noreferrer" className="provision-link">
            {status.run.name} → {status.run.status}{status.run.conclusion ? ` (${status.run.conclusion})` : ""}
          </a>
        )}
      </div>
      {err && <div className="error-banner">{err}</div>}
      <div className="modal-actions">
        <button onClick={onCancel}>
          {state === "ready" || state === "failed" ? "Close" : "Run in background"}
        </button>
      </div>
    </>
  );
}
